// Small, dependency-free replacements for the handful of lodash helpers the app
// uses. Signatures match lodash so call sites stay unchanged. `debounce` lives in
// its own file and is re-exported here so everything imports from one place.
export { default as debounce } from './debounce'

export function isObject(value) {
    return value !== null && typeof value === 'object'
}

export function isEmpty(value) {
    if (value == null) return true
    if (Array.isArray(value) || typeof value === 'string') return value.length === 0
    if (value instanceof Map || value instanceof Set) return value.size === 0
    if (typeof value === 'object') return Object.keys(value).length === 0
    return true
}

export function clamp(value, lower, upper) {
    return Math.min(Math.max(value, lower), upper)
}

export function cloneDeep(value) {
    if (!isObject(value)) return value
    try {
        return structuredClone(value)
    } catch (e) {
        // structuredClone throws on functions / DOM nodes — fall back to a JSON clone
        return JSON.parse(JSON.stringify(value))
    }
}

// lodash `forEach`: iterates arrays as (value, index) and objects as (value, key)
export function forEach(collection, iteratee) {
    if (collection == null) return collection
    if (Array.isArray(collection)) {
        for (let i = 0; i < collection.length; i++) {
            if (iteratee(collection[i], i, collection) === false) break
        }
    } else {
        const keys = Object.keys(collection)
        for (let i = 0; i < keys.length; i++) {
            if (iteratee(collection[keys[i]], keys[i], collection) === false) break
        }
    }
    return collection
}

// lodash `map`: maps arrays as (value, index) and objects as (value, key), returns an array
export function map(collection, iteratee) {
    if (collection == null) return []
    if (Array.isArray(collection)) return collection.map(iteratee)
    return Object.keys(collection).map((key) => iteratee(collection[key], key, collection))
}

export function isEqual(a, b) {
    if (a === b) return true
    if (a == null || b == null) return a === b
    if (typeof a !== 'object' || typeof b !== 'object') return a === b

    const aIsArray = Array.isArray(a)
    const bIsArray = Array.isArray(b)
    if (aIsArray !== bIsArray) return false

    if (aIsArray) {
        if (a.length !== b.length) return false
        return a.every((item, index) => isEqual(item, b[index]))
    }

    if (a instanceof Date && b instanceof Date) return a.getTime() === b.getTime()

    const aKeys = Object.keys(a)
    const bKeys = Object.keys(b)
    if (aKeys.length !== bKeys.length) return false
    return aKeys.every((key) => Object.prototype.hasOwnProperty.call(b, key) && isEqual(a[key], b[key]))
}

// lodash `merge`: deep-merges sources into target, mutating and returning target
export function merge(target, ...sources) {
    sources.forEach((source) => {
        if (!isObject(source)) return
        Object.keys(source).forEach((key) => {
            const sourceValue = source[key]
            const targetValue = target[key]
            const bothPlainObjects = isObject(sourceValue) && !Array.isArray(sourceValue)
                && isObject(targetValue) && !Array.isArray(targetValue)
            if (bothPlainObjects) {
                merge(targetValue, sourceValue)
            } else if (sourceValue !== undefined) {
                target[key] = sourceValue
            }
        })
    })
    return target
}

// lodash `unionBy`: dedupes the merged arrays by an iteratee, keeping first occurrence.
// iteratee may be a property name (string) or a function.
export function unionBy(...args) {
    const iteratee = args.pop()
    const getKey = typeof iteratee === 'function' ? iteratee : (item) => item?.[iteratee]
    const seen = new Set()
    const result = []
    args.forEach((arr) => {
        (arr || []).forEach((item) => {
            const key = getKey(item)
            if (!seen.has(key)) {
                seen.add(key)
                result.push(item)
            }
        })
    })
    return result
}

// lodash `orderBy`: sorts by one or more iteratees (property names or functions)
// with matching directions ('asc' | 'desc'). A single direction applies to all.
export function orderBy(collection, iteratees, orders) {
    const its = (Array.isArray(iteratees) ? iteratees : [iteratees]).map(
        (it) => (typeof it === 'function' ? it : (item) => item?.[it]),
    )
    const dirs = Array.isArray(orders) ? orders : [orders]

    return [...(collection || [])].sort((a, b) => {
        for (let i = 0; i < its.length; i++) {
            const av = its[i](a)
            const bv = its[i](b)
            const dir = (dirs[i] || dirs[0] || 'asc') === 'desc' ? -1 : 1
            if (av < bv) return -1 * dir
            if (av > bv) return 1 * dir
        }
        return 0
    })
}

// lodash `transform`: reduces a collection into an accumulator (object by default)
export function transform(object, iteratee, accumulator = {}) {
    forEach(object, (value, key) => iteratee(accumulator, value, key, object))
    return accumulator
}
